
import React, { useRef, useState, useEffect } from 'react';
import { Card, Button } from '../components/UI';
import { Download, Monitor, Smartphone, Ratio, Calendar, Palette, Grid, LayoutGrid, Eye, Tablet, Tv, AlignJustify, Type, Layers, Trophy, Highlighter, AlertTriangle, RefreshCw, Flag, List, Timer, Activity, X, User, Image as ImageIcon, Swords, FileText, BarChart2 } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { apiRequest } from '../utils/api';
import { useLeague } from '../components/LeagueContext';

type GraphicType = 'SEASON_LINEUP' | 'RACE_GRID' | 'CALENDAR' | 'STANDINGS' | 'DRIVER_STANDINGS' | 'TEAM_STANDINGS' | 'RACE_RESULTS' | 'RACE_RESULTS_DETAILED' | 'QUALI_RESULTS' | 'SEASON_STATS' | 'HEAD_TO_HEAD' | 'TEAMMATE_BATTLE' | 'DRIVER_STATS' | 'LEAGUE_STATS';
type H2HMode = 'DRIVER' | 'TEAM';
type RatioType = '16:9' | '4:3' | '4:5' | '5:4' | '3:4';
interface RatioConfig { w: number; h: number; name: string; icon: any; }

const RATIOS: Record<RatioType, RatioConfig> = {
    '16:9': { w: 1920, h: 1080, name: 'Landscape', icon: Monitor },
    '4:3': { w: 1600, h: 1200, name: 'Standard', icon: Tv },
    '3:4': { w: 1200, h: 1600, name: 'Portrait', icon: Tablet },
    '4:5': { w: 1080, h: 1350, name: 'Story', icon: Smartphone },
    '5:4': { w: 1500, h: 1200, name: 'Social', icon: Layers },
};

export const GraphicsCenter: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { settings } = useLeague();
    
    const [activeTab, setActiveTab] = useState<GraphicType>('SEASON_LINEUP');
    const [calendarView, setCalendarView] = useState<'GRID' | 'LIST'>('GRID');
    const [ratio, setRatio] = useState<RatioType>('16:9');
    const [scaleFactor, setScaleFactor] = useState(1);
    
    const [bgType, setBgType] = useState<'DARK' | 'SOLID' | 'IMAGE' | 'TRANSPARENT' | 'F1_CLEAN'>('DARK');
    const [bgColor, setBgColor] = useState('#0f0f13');
    const [customBgImage, setCustomBgImage] = useState<string | null>(null);
    const [accentColor, setAccentColor] = useState('#e10600'); 
    const [textColor, setTextColor] = useState('#ffffff');
    const [boxOpacity, setBoxOpacity] = useState<number>(0.90); 

    const [teams, setTeams] = useState<any[]>([]); 
    const [drivers, setDrivers] = useState<any[]>([]);
    const [events, setEvents] = useState<any[]>([]);
    const [standings, setStandings] = useState<any[]>([]);
    const [constructorStandings, setConstructorStandings] = useState<any[]>([]);
    const [results, setResults] = useState<any[]>([]);
    const [eventPenalties, setEventPenalties] = useState<any[]>([]);
    const [matrix, setMatrix] = useState<any>(null);
    const [leagueStatsData, setLeagueStatsData] = useState<any>(null);
    const [driverStatsData, setDriverStatsData] = useState<any>(null);
    const [seasonName, setSeasonName] = useState('Season');
    
    const [selectedEventId, setSelectedEventId] = useState<string>('');
    const [selectedSession, setSelectedSession] = useState<'RACE' | 'SPRINT'>('RACE');
    const [selectedDriverId, setSelectedDriverId] = useState<string>('');
    const [h2hMode, setH2HMode] = useState<H2HMode>('DRIVER');
    const [h2hId1, setH2HId1] = useState<string>('');
    const [h2hId2, setH2HId2] = useState<string>('');
    const [gridData, setGridData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>('');

    // Auto-Scale Logic
    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                const containerW = containerRef.current.clientWidth;
                const targetW = RATIOS[ratio].w;
                const newScale = Math.min(1, (containerW - 40) / targetW);
                setScaleFactor(newScale);
            }
        };
        
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [ratio]);

    // Helper to try paths: "teams.php" OR "php/teams.php"
    const fetchSafe = async (endpoint: string) => {
        try {
            return await apiRequest<any>(endpoint);
        } catch (e) {
            // Try with prefix if not already present
            if (!endpoint.startsWith('php/')) {
                console.log(`Retrying ${endpoint} with php/ prefix...`);
                return await apiRequest<any>(`php/${endpoint}`);
            }
            throw e;
        }
    };

    // Data Loading with Fallback Paths
    const loadData = async () => {
        setLoading(true);
        setErrorMsg('');

        try {
            // 1. Teams
            try {
                const teamsRes = await fetchSafe('teams.php');
                const tData = Array.isArray(teamsRes) ? teamsRes : (teamsRes.data || []);
                setTeams(tData);
            } catch (e: any) {
                console.error("Teams failed", e);
                setErrorMsg(prev => prev + ` Teams: ${e.message}`);
            }

            // 2. Standings
            try {
                const stdRes = await fetchSafe('standings.php');
                const sData = stdRes.data || stdRes;
                if (sData) {
                    setDrivers(sData.drivers || []);
                    setSeasonName(sData.seasonName || 'Current Season');
                    
                    // Drivers
                    setStandings([...(sData.drivers || [])].sort((a:any, b:any) => parseFloat(b.totalPoints) - parseFloat(a.totalPoints)));
                    
                    // Constructors
                    if (sData.constructors) {
                        setConstructorStandings(sData.constructors);
                    }
                }
            } catch (e: any) {
                console.error("Standings failed", e);
            }

            // 3. Events
            try {
                const evRes = await fetchSafe('events.php');
                const eData = Array.isArray(evRes) ? evRes : (evRes.data || []);
                
                const processedEvents = eData.map((e: any) => ({
                    ...e,
                    isSprint: !!e.isSprint,
                    isTestRace: e.isTestRace === true || e.isTestRace === '1' || e.isTestRace === 1
                }));
                setEvents(processedEvents);
                
                // Default to last completed event for results, or first upcoming for entry list
                const completed = processedEvents.filter((e: any) => e.status === 'COMPLETED');
                const upcoming = processedEvents.filter((e: any) => e.status !== 'COMPLETED');
                
                if (completed.length > 0) {
                    setSelectedEventId(completed[completed.length - 1].id);
                } else if (upcoming.length > 0) {
                    setSelectedEventId(upcoming[0].id);
                } else if (processedEvents.length > 0) {
                    setSelectedEventId(processedEvents[0].id);
                }
            } catch (e: any) {
                console.error("Events failed", e);
                setErrorMsg(prev => prev + ` Events: ${e.message}`);
            }

            // 4. Matrix & League Stats (Caster Graphics)
            try {
                const [mData, lData] = await Promise.all([
                    fetchSafe('season_matrix.php'),
                    fetchSafe('stats_rankings.php')
                ]);
                if (mData) setMatrix(mData);
                if (lData) setLeagueStatsData(lData);
            } catch (e) {
                console.error("Matrix/Stats failed", e);
            }

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (selectedEventId) {
            const ev = events.find(e => e.id === selectedEventId);
            if (ev && !ev.isSprint) {
                setSelectedSession('RACE');
            }
        }
    }, [selectedEventId, events]);

    useEffect(() => {
        if (selectedEventId) {
            const fetchEventSpecific = async () => {
                setLoading(true);
                const promises = [];
                
                // Results
                if (['RACE_RESULTS', 'RACE_RESULTS_DETAILED', 'QUALI_RESULTS'].includes(activeTab)) {
                    setResults([]);
                    setEventPenalties([]);
                    promises.push(
                        fetchSafe(`results.php?eventId=${selectedEventId}`)
                            .then(res => {
                                const data = Array.isArray(res) ? res : (res?.data || res?.results || []);
                                setResults(data);
                            })
                            .catch(() => setResults([]))
                    );
                    
                    if (activeTab === 'RACE_RESULTS_DETAILED') {
                        promises.push(
                            fetchSafe(`penalties.php?eventId=${selectedEventId}`)
                                .then(res => {
                                    const data = Array.isArray(res) ? res : (res?.data || res?.penalties || []);
                                    setEventPenalties(data);
                                })
                                .catch(() => setEventPenalties([]))
                        );
                    }
                }

                // Grid
                if (activeTab === 'RACE_GRID') {
                    setGridData([]);
                    promises.push(
                        fetchSafe(`event_entries.php?eventId=${selectedEventId}`)
                            .then(res => {
                                const data = res.savedEntries || res.data?.savedEntries || (Array.isArray(res) ? res : (res.data || []));
                                setGridData(data);
                            })
                            .catch(() => setGridData([]))
                    );
                }

                await Promise.all(promises);
                setLoading(false);
            };
            fetchEventSpecific();
        }
    }, [selectedEventId, activeTab, selectedSession]);

    // Driver Stats Loader
    useEffect(() => {
        if (selectedDriverId && activeTab === 'DRIVER_STATS') {
            const fetchDriverStats = async () => {
                setLoading(true);
                try {
                    const res = await fetchSafe(`driver_stats.php?driverId=${selectedDriverId}`);
                    if (res) setDriverStatsData(res);
                } catch (e) { console.error(e); }
                finally { setLoading(false); }
            };
            fetchDriverStats();
        }
    }, [selectedDriverId, activeTab]);

    useEffect(() => {
        const t = setTimeout(() => drawCanvas(), 100);
        return () => clearTimeout(t);
    }, [activeTab, calendarView, ratio, bgType, bgColor, textColor, customBgImage, accentColor, boxOpacity, teams, gridData, settings, selectedEventId, selectedSession, events, constructorStandings, results, eventPenalties, matrix, leagueStatsData, driverStatsData, h2hMode, h2hId1, h2hId2, selectedDriverId]);

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return `${baseUrl}/${url.startsWith('/') ? url.slice(1) : url}`;
    };

    const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "Anonymous"; 
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Failed to load ${url}`));
            img.src = getUrl(url) || "";
        });
    };

    const resolveTeamLogo = (item: any) => {
        if (!item) return undefined;
        return item.teamLogo || item.logoUrl || item.logo_url;
    };

    const resolveCarImage = (item: any) => {
        if (!item) return undefined;
        return item.carImageUrl || item.car_image_url;
    };

    const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
        if (w < 2 * r) r = w / 2;
        if (h < 2 * r) r = h / 2;
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
    };

    const parseTimeStr = (str: string): number => {
        if (!str || typeof str !== 'string') return 0;
        try {
            const parts = str.split(':');
            if (parts.length === 2) {
                return (parseInt(parts[0]) * 60) + parseFloat(parts[1]);
            }
            return parseFloat(str) || 0;
        } catch { return 0; }
    };

    const drawCleanF1Background = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        ctx.fillStyle = '#f8f8f8';
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#e0e0e0';
        for (let y = 0; y < h; y += 30) {
            for (let x = 0; x < w; x += 30) {
                if (x < 600 || x > w - 600) {
                    ctx.beginPath();
                    ctx.arc(x, y, 2, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
        }
        ctx.fillStyle = '#000000';
        ctx.beginPath(); ctx.moveTo(w - 400, 0); ctx.lineTo(w, 0); ctx.lineTo(w, 400); ctx.lineTo(w - 150, 0); ctx.fill();
        ctx.beginPath(); ctx.moveTo(0, h); ctx.lineTo(400, h); ctx.lineTo(0, h - 400); ctx.fill();
        ctx.fillStyle = accentColor;
        ctx.fillRect(0, 120, w, 10);
        ctx.fillRect(0, h - 40, w, 10);
    };

    const drawDriverStandings = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "DRIVER STANDINGS", "World Championship", w);
        const startY = 220; const leftX = 100; const leftW = 950; const rowH_Big = 65; const gap_Big = 15;
        const rightX = 1100; const rightW = 720; const rowH_Small = 50; const gap_Small = 12;
        const data = drivers.slice(0, 22);
        
        for (let i = 0; i < data.length; i++) {
            const d = data[i]; const isTop10 = i < 10;
            const currentX = isTop10 ? leftX : rightX; const currentW = isTop10 ? leftW : rightW;
            const currentH = isTop10 ? rowH_Big : rowH_Small; const currentGap = isTop10 ? gap_Big : gap_Small;
            let currentY = startY + i * (currentH + currentGap);
            if (!isTop10) { const offsetIdx = i - 10; currentY = startY + offsetIdx * (currentH + currentGap); }
            const skew = 20;
            const rankW = isTop10 ? 80 : 60;

            ctx.fillStyle = i===0 ? accentColor : i<3 ? '#333' : '#ccc';
            ctx.beginPath(); ctx.moveTo(currentX + skew, currentY); ctx.lineTo(currentX + rankW + skew, currentY); ctx.lineTo(currentX + rankW, currentY + currentH); ctx.lineTo(currentX, currentY + currentH); ctx.fill();
            ctx.fillStyle = (i === 0) ? textColor : (i < 3 ? '#fff' : '#000'); ctx.font = `900 ${isTop10 ? '36' : '28'}px "Titillium Web"`; ctx.textAlign = 'center'; ctx.fillText((i + 1).toString(), currentX + rankW/2 + skew/2, currentY + currentH * 0.7);
            
            const barX = currentX + rankW + (isTop10 ? 10 : 5); const fullBarW = currentW - rankW - (isTop10 ? 150 : 100);
            ctx.save(); ctx.globalAlpha = boxOpacity; ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.moveTo(barX + skew, currentY); ctx.lineTo(barX + fullBarW + skew, currentY); ctx.lineTo(barX + fullBarW, currentY + currentH); ctx.lineTo(barX, currentY + currentH); ctx.fill(); ctx.restore();
            ctx.fillStyle = d.teamColor || '#666'; ctx.beginPath(); ctx.moveTo(barX + skew, currentY); ctx.lineTo(barX + skew + 8, currentY); ctx.lineTo(barX + 8, currentY + currentH); ctx.lineTo(barX, currentY + currentH); ctx.fill();
            
            ctx.textAlign = 'left'; ctx.fillStyle = '#000'; ctx.font = `bold ${isTop10 ? '32' : '24'}px "Titillium Web"`; ctx.fillText(String(d.username).toUpperCase(), barX + 30, currentY + currentH * 0.68);
            if (isTop10) { ctx.textAlign = 'right'; ctx.fillStyle = '#666'; ctx.font = 'bold 18px "Titillium Web"'; ctx.fillText(d.teamName?.toUpperCase() || "", barX + fullBarW - 20, currentY + currentH * 0.65); }
                
            const tLogo = resolveTeamLogo(d);
            if (tLogo) { try { const img = await loadImage(tLogo); const scale = (currentH * 0.6) / img.height; if (isTop10) ctx.drawImage(img, barX + (fullBarW/2) + 50, currentY + (currentH*0.2), img.width * scale, currentH * 0.6); else ctx.drawImage(img, barX + fullBarW - (img.width * scale) - 10, currentY + (currentH*0.2), img.width * scale, currentH * 0.6); } catch {} }

            const ptsX = barX + fullBarW + (isTop10 ? 10 : 5); const ptsW = isTop10 ? 120 : 80;
            ctx.fillStyle = '#000'; ctx.beginPath(); ctx.moveTo(ptsX + skew, currentY); ctx.lineTo(ptsX + ptsW + skew, currentY); ctx.lineTo(ptsX + ptsW, currentY + currentH); ctx.lineTo(ptsX, currentY + currentH); ctx.fill();
            ctx.fillStyle = accentColor; ctx.textAlign = 'center'; ctx.font = `900 ${isTop10 ? '36' : '28'}px "Titillium Web"`; ctx.fillText(Math.round(d.totalPoints).toString(), ptsX + ptsW/2 + skew/2, currentY + currentH * 0.7);
        }
    };

    const drawTeamStandings = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "CONSTRUCTORS", "World Championship", w);
        const startY = 220; const rowH = 75; const gap = 12;
        
        const data = constructorStandings.length > 0 ? constructorStandings : teams;

        for (let i = 0; i < data.length; i++) {
            const tData = data[i]; const y = startY + i * (rowH + gap); const skew = 20;
            ctx.beginPath(); ctx.moveTo(100 + skew, y); ctx.lineTo(w - 300 + skew, y); ctx.lineTo(w - 300, y + rowH); ctx.lineTo(100, y + rowH); 
            ctx.save(); ctx.globalAlpha = boxOpacity; ctx.fillStyle = '#fff'; ctx.fill(); ctx.restore();
            
            ctx.fillStyle = tData.teamColor || tData.color || '#fff'; ctx.fillRect(100, y + rowH - 6, w - 400, 6);
            ctx.fillStyle = '#000'; ctx.font = '900 48px "Titillium Web"'; ctx.textAlign = 'left'; ctx.fillText((i+1).toString(), 140, y + 55);
            
            let nameOffsetX = 220; 
            const logoUrl = resolveTeamLogo(tData);
            if (logoUrl) { try { const l = await loadImage(logoUrl); const s = Math.min(50 / l.width, 50 / l.height); ctx.drawImage(l, nameOffsetX, y + 12, l.width*s, l.height*s); nameOffsetX += (l.width*s) + 20; } catch {} }
            ctx.fillStyle = '#000'; ctx.font = 'bold 42px "Titillium Web"'; ctx.fillText(tData.name.toUpperCase(), nameOffsetX, y + 52);
            
            const carImgUrl = resolveCarImage(tData);
            if(carImgUrl) { try { const car = await loadImage(carImgUrl); const maxH = rowH * 0.9; const ratio = maxH / car.height; const cW = car.width * ratio; const carX = w - 300 - cW - 20; ctx.save(); ctx.shadowColor = 'black'; ctx.shadowBlur = 5; ctx.drawImage(car, carX, y + (rowH-maxH)/2, cW, maxH); ctx.restore(); } catch{} }
            
            const ptsX = w - 280; ctx.fillStyle = '#000'; ctx.beginPath(); ctx.moveTo(ptsX + skew, y); ctx.lineTo(ptsX + 150 + skew, y); ctx.lineTo(ptsX + 150, y + rowH); ctx.lineTo(ptsX, y + rowH); ctx.fill();
            ctx.fillStyle = accentColor; ctx.textAlign = 'center'; ctx.font = '900 42px "Titillium Web"'; ctx.fillText(Math.round(tData.totalPoints || 0).toString(), ptsX + 75 + skew/2, y + 52);
        }
    };

    const drawRaceResults = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        const ev = events.find(e => e.id === selectedEventId);
        const title = ev ? `${ev.trackLocation.toUpperCase()}${selectedSession === 'SPRINT' ? ' SPRINT' : ''}` : "RACE RESULTS";
        drawHeader(ctx, title, "", w);
        
        if (!results || results.length === 0) {
            drawNoData(ctx, w, h, "NO RESULTS DATA");
            return;
        }
        
        const posField = selectedSession === 'SPRINT' ? 'sprint_position' : 'position';
        const statusField = selectedSession === 'SPRINT' ? 'sprint_status' : 'status';
        
        const filtered = results
            .filter(r => (Number(r[posField]) || Number(r.position) || Number(r.pos)) > 0)
            .sort((a,b) => {
                const posA = Number(a[posField]) || Number(a.position) || Number(a.pos) || 99;
                const posB = Number(b[posField]) || Number(b.position) || Number(b.pos) || 99;
                return posA - posB;
            });

        if (filtered.length === 0) {
            drawNoData(ctx, w, h, "NO CLASSIFIED FINISHERS");
            return;
        }
        
        const podiumY = 220; const pW = 220; const pH = 320; const centerX = w / 2; const posOrder = [1, 0, 2]; const xOffsets = [-pW - 30, 0, pW + 30]; 
        
        for (let i = 0; i < 3; i++) {
            const rIdx = posOrder[i]; 
            if (rIdx >= filtered.length) continue; 
            const r = filtered[rIdx]; const x = centerX + xOffsets[i] - (pW/2); 
            const finalY = podiumY + [50, 0, 80][i];

            ctx.save();
            ctx.fillStyle = '#fff'; ctx.fillRect(x, finalY, pW, pH); ctx.strokeStyle = '#333'; ctx.lineWidth = 2; ctx.strokeRect(x, finalY, pW, pH);
            ctx.fillStyle = r.teamColor || '#333'; ctx.fillRect(x, finalY + pH - 10, pW, 10);
            
            const rLogo = resolveTeamLogo(r);
            if (rLogo) { try { const l = await loadImage(rLogo); const s = Math.min(80/l.width, 60/l.height); ctx.drawImage(l, x + (pW - l.width*s)/2, finalY + 140, l.width*s, l.height*s); } catch{} }
            
            ctx.fillStyle = '#000'; ctx.textAlign='center'; ctx.font = '900 120px "Titillium Web"'; ctx.fillText((rIdx+1).toString(), x+pW/2, finalY+130);
            ctx.font = 'bold 24px "Titillium Web"'; ctx.fillText(String(r.username).toUpperCase(), x+pW/2, finalY+240);
            ctx.restore();
        }
        
        const listStartY = 650; const listX = 100; const listW = w - 200; const listRowH = 40; const colGap = 40;
        const rest = filtered.slice(3); const mid = Math.ceil(rest.length / 2);
        
        ctx.save();
        for (let i = 0; i < rest.length; i++) {
            const r = rest[i]; const col = i < mid ? 0 : 1; const row = i % mid; 
            const colW = (listW / 2) - (colGap / 2); const lx = listX + (col * (colW + colGap)); 
            const finalLy = listStartY + (row * (listRowH + 6));

            const currentStatus = r[statusField] || 'FINISHED';

            ctx.fillStyle = '#fff'; ctx.fillRect(lx, finalLy, colW, listRowH);
            ctx.fillStyle = currentStatus === 'FINISHED' ? '#222' : '#aa0000'; ctx.fillRect(lx, finalLy, 50, listRowH);
            ctx.fillStyle = '#fff'; ctx.font = 'bold 20px "Titillium Web"'; ctx.textAlign = 'center'; ctx.fillText(currentStatus === 'FINISHED' ? (i + 4).toString() : currentStatus.substring(0,3), lx + 25, finalLy + 28);
            ctx.fillStyle = '#000'; ctx.textAlign = 'left'; ctx.fillText(String(r.username).toUpperCase(), lx + 60, finalLy + 28);
        }
        ctx.restore();
    };

    const drawDetailedRaceResults = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        const ev = events.find(e => e.id === selectedEventId);
        const trackName = ev ? ev.trackLocation.toUpperCase() : "GRAND PRIX";
        const sessionName = selectedSession === 'SPRINT' ? 'SPRINT CLASSIFICATION' : 'OFFICIAL CLASSIFICATION';
        
        ctx.save();
        ctx.fillStyle = '#000'; ctx.fillRect(0, 0, w, 150); 
        ctx.fillStyle = '#fff'; ctx.font = '900 60px "Titillium Web"'; ctx.textAlign = 'left'; ctx.fillText(sessionName, 50, 80);
        ctx.fillStyle = '#999'; ctx.font = 'bold 30px "Titillium Web"'; ctx.fillText(trackName, 50, 130);
        ctx.fillStyle = accentColor; ctx.fillRect(0, 150, w, 10); ctx.fillRect(0, h-20, w, 20);
        ctx.restore();

        if (!results || results.length === 0) {
            drawNoData(ctx, w, h, "NO RESULTS DATA");
            return;
        }

        const posField = selectedSession === 'SPRINT' ? 'sprint_position' : 'position';
        const timeField = selectedSession === 'SPRINT' ? 'sprint_time' : 'time_diff';
        const statusField = selectedSession === 'SPRINT' ? 'sprint_status' : 'status';

        const filtered = results
            .filter(r => (Number(r[posField]) || Number(r.position) || Number(r.pos)) > 0)
            .sort((a,b) => {
                const posA = Number(a[posField]) || Number(a.position) || Number(a.pos) || 99;
                const posB = Number(b[posField]) || Number(b.position) || Number(b.pos) || 99;
                return posA - posB;
            });

        if (filtered.length === 0) {
            drawNoData(ctx, w, h, "NO CLASSIFIED FINISHERS");
            return;
        }

        const marginX = 50; const colW = (w - (marginX * 3)) / 2; const startY = 200; const rowH = 65; const gap = 15; const maxRowsPerCol = 10; 

        for(let i=0; i<filtered.length; i++) {
            if (i >= 20) break;
            const r = filtered[i]; const colIndex = Math.floor(i / maxRowsPerCol); const rowIndex = i % maxRowsPerCol;
            const x = marginX + (colIndex * (colW + marginX)); const y = startY + (rowIndex * (rowH + gap));

            ctx.save();
            ctx.fillStyle = '#15151e'; ctx.fillRect(x, y, colW, rowH);
            
            const currentStatus = r[statusField] || 'FINISHED';
            const currentPos = Number(r[posField]);

            let posColor = '#333'; let posText = String(currentPos);
            if (currentStatus !== 'FINISHED') { posColor = '#333'; posText = "NC"; } 
            else {
                if (currentPos === 1) posColor = '#fff200';
                else if (currentPos === 2) posColor = '#dadada';
                else if (currentPos === 3) posColor = '#d45600';
                else if (currentPos <= 10) posColor = '#0090d0'; 
            }
            if (currentStatus !== 'FINISHED' && currentStatus !== 'DNS') { posColor = '#880000'; }

            ctx.fillStyle = posColor; ctx.fillRect(x, y, 50, rowH);
            ctx.fillStyle = (i===0 || i===1 || i===2 && currentStatus==='FINISHED') ? '#000' : '#fff';
            ctx.font = '900 28px "Titillium Web"'; ctx.textAlign = 'center'; ctx.fillText(posText, x + 25, y + 42);

            ctx.fillStyle = r.teamColor || '#666'; ctx.fillRect(x + 50, y, 6, rowH);

            const rLogo = resolveTeamLogo(r);
            if (rLogo) { try { const logo = await loadImage(rLogo); const s = 30 / logo.height; ctx.drawImage(logo, x + 65, y + 17, logo.width * s, 30); } catch {} }

            ctx.textAlign = 'left'; ctx.fillStyle = '#fff'; ctx.font = 'bold 24px "Titillium Web"'; ctx.fillText(String(r.username).toUpperCase(), x + 110, y + 42);
            
            const teamObj = teams.find(t => t.id === r.team_id || t.name === r.teamName);
            const carUrl = resolveCarImage(teamObj);
            if (carUrl) { try { const car = await loadImage(carUrl); const carH = rowH - 10; const carRatio = carH / car.height; const carW = car.width * carRatio; ctx.drawImage(car, x + 300, y + 5, carW, carH); } catch {} }

            ctx.textAlign = 'right'; ctx.font = 'mono 20px "Titillium Web"'; ctx.fillStyle = '#fff';
            let timeStr = r[timeField]; if (currentStatus !== 'FINISHED') timeStr = currentStatus; else if (i === 0) timeStr = r[timeField]; 
            ctx.fillText(timeStr || '-', x + colW - 60, y + 40);

            let penStr = "";
            const manualPens = eventPenalties.filter(p => p.user_id === r.user_id && p.time_penalty > 0);
            const manualSecs = manualPens.reduce((sum, p) => sum + parseInt(p.time_penalty), 0);
            if(manualSecs > 0) penStr = `+${manualSecs}s`;
             try { if (r.telemetry_data) { const t = typeof r.telemetry_data === 'string' ? JSON.parse(r.telemetry_data) : r.telemetry_data; if (t.penalties > 0 && !penStr) penStr = `+${t.penalties}s`; } } catch {}

            if (penStr) { ctx.fillStyle = '#ef4444'; ctx.font = 'bold 14px "Titillium Web"'; ctx.fillText(penStr, x + colW - 60, y + 18); }

            ctx.fillStyle = accentColor; ctx.font = 'bold 24px "Titillium Web"'; ctx.textAlign = 'center';
            
            // Points display logic
            let displayPoints = r.points || 0;
            if (selectedSession === 'SPRINT' && settings?.points_sprint) {
                displayPoints = settings.points_sprint[currentPos - 1] || 0;
            }

            if (displayPoints > 0) { ctx.fillText(String(displayPoints), x + colW - 25, y + 42); }

            let badgeX = x + colW - 180; 
            if (r.fastest_lap == 1 || r.fastest_lap === true || r.fastest_lap === 'true') { ctx.beginPath(); ctx.arc(badgeX, y + rowH/2, 12, 0, Math.PI*2); ctx.fillStyle = '#a855f7'; ctx.fill(); ctx.fillStyle='#fff'; ctx.font='bold 10px "Titillium Web"'; ctx.fillText("FL", badgeX, y + rowH/2 + 3); badgeX -= 30; }
             if (r.is_dotd == 1 || r.is_dotd === true || r.is_dotd === 'true') { ctx.beginPath(); ctx.arc(badgeX, y + rowH/2, 12, 0, Math.PI*2); ctx.fillStyle = '#eab308'; ctx.fill(); ctx.fillStyle='#000'; ctx.font='bold 9px "Titillium Web"'; ctx.fillText("DOTD", badgeX, y + rowH/2 + 3); }
            ctx.restore();
        }
    };

    const drawQualiResults = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        const ev = events.find(e => e.id === selectedEventId);
        const title = ev ? `QUALIFYING: ${ev.trackLocation.toUpperCase()}` : "QUALIFYING RESULTS";
        drawHeader(ctx, title, "Session Classification", w);

        if (!results || results.length === 0) {
            drawNoData(ctx, w, h, "NO RESULTS DATA");
            return;
        }
        const qResults = results
            .filter(r => (Number(r.quali_position) || Number(r.position) || Number(r.pos)) > 0)
            .sort((a,b) => {
                const posA = Number(a.quali_position) || Number(a.position) || Number(a.pos) || 99;
                const posB = Number(b.quali_position) || Number(b.position) || Number(b.pos) || 99;
                return posA - posB;
            });
            
        if (qResults.length === 0) {
            drawNoData(ctx, w, h, "NO QUALIFYING TIMES FOUND");
            return;
        }

        const poleTime = parseTimeStr(qResults[0].quali_time);
        let maxGap = 0;
        if (poleTime > 0) {
            qResults.forEach(r => {
                const t = parseTimeStr(r.quali_time);
                if (t > poleTime) {
                    const gap = t - poleTime;
                    if (gap > maxGap) maxGap = gap;
                }
            });
        }
        if (maxGap === 0) maxGap = 2;

        const startY = 220; const leftX = 100; const leftW = 950; const rowH = 60; const gap = 15; const rightX = 1100; const rightW = 720;
        
        for (let i = 0; i < qResults.length; i++) {
            if (i >= 20) break;
            const r = qResults[i]; 
            const isTop10 = i < 10;
            const currentX = isTop10 ? leftX : rightX; 
            const currentW = isTop10 ? leftW : rightW;
            let currentY = startY + i * (rowH + gap); 
            if (!isTop10) { const offsetIdx = i - 10; currentY = startY + offsetIdx * (rowH + gap); }
            
            const skew = 20; const rankW = isTop10 ? 80 : 60;
            
            ctx.fillStyle = i===0 ? '#a855f7' : '#333';
            ctx.beginPath(); ctx.moveTo(currentX + skew, currentY); ctx.lineTo(currentX + rankW + skew, currentY); ctx.lineTo(currentX + rankW, currentY + rowH); ctx.lineTo(currentX, currentY + rowH); ctx.fill();
            ctx.fillStyle = '#fff'; ctx.font = `900 ${isTop10 ? '32' : '26'}px "Titillium Web"`; ctx.textAlign = 'center'; ctx.fillText(r.quali_position.toString(), currentX + rankW/2 + skew/2, currentY + rowH * 0.7);

            const barX = currentX + rankW + 10; const fullBarW = currentW - rankW - 120; 
            ctx.save(); ctx.globalAlpha = boxOpacity; ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.moveTo(barX + skew, currentY); ctx.lineTo(barX + fullBarW + skew, currentY); ctx.lineTo(barX + fullBarW, currentY + rowH); ctx.lineTo(barX, currentY + rowH); ctx.fill(); ctx.restore();
            
            ctx.fillStyle = r.teamColor || '#666'; ctx.beginPath(); ctx.moveTo(barX + skew, currentY); ctx.lineTo(barX + skew + 8, currentY); ctx.lineTo(barX + 8, currentY + rowH); ctx.lineTo(barX, currentY + rowH); ctx.fill();

            ctx.textAlign = 'left'; ctx.fillStyle = '#000'; ctx.font = `bold ${isTop10 ? '28' : '22'}px "Titillium Web"`; ctx.fillText(String(r.username).toUpperCase(), barX + 30, currentY + rowH * 0.68);
            if (isTop10) { ctx.textAlign = 'right'; ctx.fillStyle = '#666'; ctx.font = 'bold 16px "Titillium Web"'; ctx.fillText(r.teamName?.toUpperCase() || "", barX + fullBarW - 20, currentY + rowH * 0.65); }

            const timeX = barX + fullBarW + 5; const timeW = 150; 
            ctx.fillStyle = '#111'; ctx.beginPath(); ctx.moveTo(timeX + skew, currentY); ctx.lineTo(timeX + timeW + skew, currentY); ctx.lineTo(timeX + timeW, currentY + rowH); ctx.lineTo(timeX, currentY + rowH); ctx.fill();

            if (i > 0 && poleTime > 0) {
                const myTime = parseTimeStr(r.quali_time);
                if (myTime > 0 && r.quali_status === 'FINISHED') {
                    const gapVal = myTime - poleTime;
                    const barWidth = (gapVal / maxGap) * (timeW + skew); 
                    ctx.save(); ctx.beginPath(); ctx.moveTo(timeX + skew, currentY); ctx.lineTo(timeX + timeW + skew, currentY); ctx.lineTo(timeX + timeW, currentY + rowH); ctx.lineTo(timeX, currentY + rowH); ctx.clip();
                    ctx.fillStyle = r.teamColor || '#666'; ctx.globalAlpha = 0.4; ctx.fillRect(timeX, currentY, barWidth, rowH); ctx.restore();
                }
            }

            ctx.fillStyle = r.quali_status === 'FINISHED' ? '#fff' : '#ef4444'; 
            ctx.textAlign = 'center'; ctx.font = 'bold 20px "Titillium Web"';
            let timeText = r.quali_time; if (r.quali_status && r.quali_status !== 'FINISHED') timeText = r.quali_status.substring(0,3); if (!timeText) timeText = "-";
            ctx.fillText(timeText, timeX + timeW/2 + skew/2, currentY + rowH * 0.68);
        }
    };

    const drawSeasonStats = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "SEASON INSIGHTS", "Performance Analytics", w);
        if (!matrix || !matrix.drivers) return;

        const stats = drivers.slice(0, 20).map(d => {
            const history = matrix.matrix[d.id] || {};
            let countRace = 0, totalQualiPos = 0, countQuali = 0, posGain = 0, podiums = 0;
            Object.values(history).forEach((r: any) => {
                const p = parseInt(r.position) || 0;
                let startPos = parseInt(r.grid_position) || 0;
                let qPos = parseInt(r.quali_position) || 0;
                if (startPos === 0 && qPos > 0) startPos = qPos;
                if (qPos > 0) { totalQualiPos += qPos; countQuali++; }
                if (r.status !== 'DNS') countRace++;
                if (r.status === 'FINISHED') {
                    if (p <= 3) podiums++;
                    if (startPos > 0 && p > 0) posGain += (startPos - p);
                }
            });
            const ppr = countRace > 0 ? (parseFloat(d.totalPoints) / countRace).toFixed(2) : "0.0";
            const avgQuali = countQuali > 0 ? (totalQualiPos / countQuali).toFixed(1) : "-";
            return { ...d, races: countRace, avgQuali, podiums, posGain, ppr };
        }).sort((a:any, b:any) => parseFloat(b.totalPoints) - parseFloat(a.totalPoints));

        const startY = 220; const colW = 850; const rowH = 45; const gapX = 40; const leftX = 80; const rightX = leftX + colW + gapX;

        const drawColHeader = (bx: number) => {
            ctx.fillStyle = '#15151e'; ctx.fillRect(bx, startY, colW, 40);
            ctx.fillStyle = '#aaa'; ctx.font = 'bold 16px "Titillium Web"'; ctx.textAlign = 'left';
            ctx.fillText("POS", bx + 20, startY + 27); ctx.fillText("DRIVER", bx + 70, startY + 27);
            ctx.fillText("RACES", bx + 430, startY + 27); ctx.fillText("AVG Q", bx + 530, startY + 27);
            ctx.fillText("PODIUMS", bx + 630, startY + 27); ctx.fillText("GAIN", bx + 710, startY + 27);
            ctx.fillText("PPR", bx + 790, startY + 27);
        };
        drawColHeader(leftX); drawColHeader(rightX);

        for (let i = 0; i < stats.length; i++) {
            const d = stats[i]; const col = i < 10 ? 0 : 1; const row = i % 10;
            const bx = col === 0 ? leftX : rightX; const y = startY + 45 + (row * (rowH + 6));
            ctx.save(); ctx.globalAlpha = boxOpacity; ctx.fillStyle = '#fff'; ctx.fillRect(bx, y, colW, rowH); ctx.restore();
            ctx.fillStyle = d.teamColor || '#666'; ctx.fillRect(bx, y, 5, rowH);
            ctx.fillStyle = '#000'; ctx.font = 'bold 18px "Titillium Web"'; ctx.textAlign = 'left';
            ctx.fillText((i + 1).toString(), bx + 20, y + 32); ctx.fillText(String(d.username).toUpperCase(), bx + 70, y + 32);
            ctx.fillStyle = '#444'; ctx.fillText(d.races.toString(), bx + 450, y + 32); ctx.fillText(d.avgQuali, bx + 550, y + 32);
            ctx.fillStyle = d.podiums > 0 ? '#d4af37' : '#ccc'; ctx.fillText(d.podiums.toString(), bx + 650, y + 32);
            const gain = d.posGain; const gainStr = gain > 0 ? `+${gain}` : gain.toString();
            ctx.fillStyle = gain > 0 ? '#10b981' : (gain < 0 ? '#ef4444' : '#888'); ctx.fillText(gainStr, bx + 730, y + 32);
            ctx.fillStyle = '#1a1a1a'; ctx.fillRect(bx + 770, y + 8, 80, rowH - 16);
            ctx.fillStyle = accentColor; ctx.font = 'bold 18px "Titillium Web"'; ctx.textAlign = 'center'; ctx.fillText(d.ppr, bx + 810, y + 30);
        }
    };

    const drawHeadToHead = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        let e1, e2;
        if (h2hMode === 'DRIVER') { e1 = drivers.find(d => d.id === h2hId1); e2 = drivers.find(d => d.id === h2hId2); } else { e1 = teams.find(t => t.id === h2hId1); e2 = teams.find(t => t.id === h2hId2); }
        drawHeader(ctx, h2hMode === 'DRIVER' ? "HEAD TO HEAD" : "TEAM BATTLE", "", w);
        const cardW = 500; const cardH = 700; const cardY = 220; const gap = 400; const x1Final = (w/2) - (gap/2) - cardW; const x2Final = (w/2) + (gap/2);
        
        const drawCard = async (x: number, ent: any) => {
            if(!ent) return;
            ctx.save();
            const color = ent.teamColor || ent.color || '#333';
            ctx.fillStyle = '#fff'; ctx.fillRect(x, cardY, cardW, cardH);
            ctx.fillStyle = color; ctx.fillRect(x, cardY, cardW, 15);
            const imgUrl = h2hMode === 'DRIVER' ? (ent.avatarUrl || ent.teamLogo) : resolveTeamLogo(ent);
            if(imgUrl) { try { const img = await loadImage(imgUrl); const s = Math.min((cardW-40)/img.width, 350/img.height); ctx.drawImage(img, x+(cardW-img.width*s)/2, cardY+50, img.width*s, img.height*s); } catch {} }
            ctx.fillStyle='#000'; ctx.textAlign='center'; ctx.font='900 50px "Titillium Web"'; ctx.fillText((ent.username || ent.name).toUpperCase(), x+cardW/2, cardY+500);
            ctx.restore();
        };
        await drawCard(x1Final, e1); await drawCard(x2Final, e2);
        
        if(e1 && e2) {
             const statsY = cardY + 150;
             const labels = h2hMode === 'DRIVER' ? ["POINTS", "WINS", "PODIUMS", "RACES"] : ["POINTS", "WINS", "PODIUMS", "PPR"];
             let d1: any[] = [], d2: any[] = [];
             if (h2hMode === 'DRIVER') { d1 = [parseFloat(e1.totalPoints||0), parseInt(e1.wins||0), parseInt(e1.podiums||0), parseInt(e1.races||0)]; d2 = [parseFloat(e2.totalPoints||0), parseInt(e2.wins||0), parseInt(e2.podiums||0), parseInt(e2.races||0)]; } 
             else {
                 const getTeamStats = (tid: string) => { const teamDrivers = drivers.filter(d => d.teamId === tid); let pods = 0; let races = 0; teamDrivers.forEach(d => { pods += parseInt(d.podiums||0); races = Math.max(races, parseInt(d.races||0)); }); const pts = parseFloat(teams.find(t=>t.id===tid)?.totalPoints||0); const ppr = races > 0 ? (pts/races).toFixed(1) : "0.0"; return { pods, ppr }; };
                 const s1 = getTeamStats(e1.id); const s2 = getTeamStats(e2.id); d1 = [parseFloat(e1.totalPoints||0), parseInt(e1.wins||0), s1.pods, parseFloat(s1.ppr as string)]; d2 = [parseFloat(e2.totalPoints||0), parseInt(e2.wins||0), s2.pods, parseFloat(s2.ppr as string)];
             }
             ctx.save();
             for(let i=0; i<4; i++) {
                 const y = statsY + (i*120); ctx.fillStyle='#666'; ctx.font='bold 24px "Titillium Web"'; ctx.textAlign='center'; ctx.fillText(labels[i], w/2, y-20);
                 ctx.font='900 60px "Titillium Web"'; ctx.textAlign='right'; ctx.fillStyle = d1[i] > d2[i] ? accentColor : '#333'; ctx.fillText(d1[i].toString(), (w/2)-60, y+30);
                 ctx.textAlign='left'; ctx.fillStyle = d2[i] > d1[i] ? accentColor : '#333'; ctx.fillText(d2[i].toString(), (w/2)+60, y+30);
             }
             ctx.restore();
        }
    };

    const drawTeammateBattle = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "TEAMMATE BATTLES", "Points Contribution", w);
        if (teams.length === 0) return;
        const startY = 220; const rowH = 140; const gap = 30; const colW = 850; const colGap = 80; const startX = (w - (colW * 2 + colGap)) / 2;
        
        for (let i = 0; i < teams.length; i++) {
            const team = teams[i]; const col = i % 2; const row = Math.floor(i / 2);
            const bx = startX + col * (colW + colGap); const by = startY + row * (rowH + gap);
            const teamDrivers = drivers.filter(d => d.teamId === team.id).slice(0, 2);
            const item = { team, d1: teamDrivers[0], d2: teamDrivers[1], p1: parseFloat(teamDrivers[0]?.totalPoints || 0), p2: parseFloat(teamDrivers[1]?.totalPoints || 0), pct1: 0.5 };
            const total = item.p1 + item.p2; if (total > 0) item.pct1 = item.p1 / total;

            ctx.save(); ctx.globalAlpha = boxOpacity; ctx.fillStyle = '#fff'; ctx.fillRect(bx, by, colW, rowH); ctx.restore();
            ctx.fillStyle = team.color || '#333'; ctx.fillRect(bx, by, 10, rowH);
            
            const logoUrl = resolveTeamLogo(team);
            if (logoUrl) { try { const l = await loadImage(logoUrl); const s = 40 / l.height; ctx.drawImage(l, bx + 20, by + 15, l.width * s, 40); } catch {} }
            ctx.fillStyle = '#000'; ctx.font = '900 24px "Titillium Web"'; ctx.textAlign = 'left'; ctx.fillText(team.name.toUpperCase(), bx + 80, by + 45);

            const barX = bx + 20; const barY = by + 60; const barW = colW - 40; const barH = 30;
            const targetW1 = barW * item.pct1;
            ctx.fillStyle = team.color || '#333'; ctx.fillRect(barX, barY, targetW1, barH);
            ctx.fillStyle = '#333'; ctx.fillRect(barX + targetW1, barY, barW - targetW1, barH);
            
            ctx.fillStyle = '#fff'; ctx.font = 'bold 18px "Titillium Web"';
            if (item.d1) { ctx.textAlign = 'left'; ctx.fillText(String(item.d1.username).toUpperCase(), barX, barY + 55); ctx.textAlign = 'center'; ctx.fillText(Math.round(item.p1).toString(), barX + targetW1/2, barY + 22); }
            if (item.d2) { ctx.textAlign = 'right'; ctx.fillText(String(item.d2.username).toUpperCase(), barX + barW, barY + 55); ctx.textAlign = 'center'; ctx.fillText(Math.round(item.p2).toString(), barX + targetW1 + (barW-targetW1)/2, barY + 22); }
            ctx.restore();
        }
    };

    const drawDriverStats = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        if (!selectedDriverId || !driverStatsData || !driverStatsData.user) { drawHeader(ctx, "DRIVER PROFILE", "Select a Driver", w); return; }
        const u = driverStatsData.user; const t = driverStatsData.team; const s = driverStatsData.stats; const color = t?.color || '#333';
        ctx.fillStyle = '#0f0f0f'; ctx.fillRect(0, 0, w, h); const grad = ctx.createLinearGradient(0, 0, w, h); grad.addColorStop(0, '#1a1a1a'); grad.addColorStop(1, '#000000'); ctx.fillStyle = grad; ctx.fillRect(0, 0, w, h); ctx.fillStyle = color; ctx.fillRect(0, 0, 30, h);
        const leftW = 600;
        if (u.avatar_url) { try { const img = await loadImage(u.avatar_url); const size = 400; ctx.save(); ctx.beginPath(); ctx.arc(leftW/2 + 50, 300, size/2, 0, Math.PI * 2); ctx.clip(); ctx.drawImage(img, leftW/2 + 50 - size/2, 300 - size/2, size, size); ctx.restore(); ctx.strokeStyle = color; ctx.lineWidth = 10; ctx.beginPath(); ctx.arc(leftW/2 + 50, 300, size/2, 0, Math.PI * 2); ctx.stroke(); } catch {} }
        ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.font = '900 80px "Titillium Web"'; ctx.fillText(String(u.username).toUpperCase(), leftW/2 + 50, 600);
        if (t) { ctx.fillStyle = color; ctx.font = 'bold 40px "Titillium Web"'; ctx.fillText(t.name.toUpperCase(), leftW/2 + 50, 660); if (t.logo_url) { try { const l = await loadImage(t.logo_url); const ls = Math.min(150/l.width, 100/l.height); ctx.drawImage(l, leftW/2 + 50 - (l.width*ls)/2, 700, l.width*ls, l.height*ls); } catch {} } }
        const rightX = 700; const rightW = w - rightX - 50;
        ctx.textAlign = 'left'; ctx.fillStyle = '#fff'; ctx.font = '900 100px "Titillium Web"'; ctx.fillText("SEASON STATS", rightX, 120); ctx.fillStyle = '#666'; ctx.font = 'bold 40px "Titillium Web"'; ctx.fillText("PERFORMANCE OVERVIEW", rightX + 5, 170);
        const statBoxW = (rightW - 40) / 2; const statBoxH = 180;
        const drawStatBox = (title: string, value: string, sub: string, bx: number, by: number) => { ctx.fillStyle = '#1e1e24'; ctx.fillRect(bx, by, statBoxW, statBoxH); ctx.fillStyle = color; ctx.fillRect(bx, by, 10, statBoxH); ctx.textAlign = 'left'; ctx.fillStyle = '#888'; ctx.font = 'bold 24px "Titillium Web"'; ctx.fillText(title.toUpperCase(), bx + 30, by + 40); ctx.fillStyle = '#fff'; ctx.font = '900 80px "Titillium Web"'; ctx.fillText(value, bx + 30, by + 130); ctx.textAlign = 'right'; ctx.fillStyle = '#666'; ctx.font = 'bold 20px "Titillium Web"'; ctx.fillText(sub, bx + statBoxW - 20, by + 160); };
        drawStatBox("Rank", s.best_pos ? `#${s.best_pos}` : '-', "Best Finish", rightX, 250); drawStatBox("Points", Math.round(s.points).toString(), "Total", rightX + statBoxW + 40, 250); drawStatBox("Podiums", s.podiums.toString(), "Top 3", rightX, 450); drawStatBox("Wins", s.wins.toString(), "P1", rightX + statBoxW + 40, 450);
        const graphY = 700; const graphH = 300; const graphW = rightW;
        ctx.fillStyle = '#111'; ctx.fillRect(rightX, graphY, graphW, graphH);
        const hist = driverStatsData.history || []; const chartData = [...hist].reverse(); 
        if (chartData.length > 1) { ctx.strokeStyle = '#333'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(rightX, graphY + graphH); ctx.lineTo(rightX + graphW, graphY + graphH); ctx.lineTo(rightX + graphW, graphY); ctx.stroke(); ctx.strokeStyle = color; ctx.lineWidth = 5; ctx.beginPath(); const stepX = graphW / (chartData.length - 1); chartData.forEach((pt: any, i: number) => { const pos = parseInt(pt.position); const px = rightX + (i * stepX); const py = graphY + ((pos / 20) * (graphH - 40)) + 20; if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py); ctx.save(); ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(px, py, 6, 0, Math.PI*2); ctx.fill(); ctx.restore(); ctx.fillStyle = '#aaa'; ctx.font = 'bold 16px "Titillium Web"'; ctx.textAlign = 'center'; ctx.fillText(`P${pos}`, px, py - 15); }); ctx.stroke(); ctx.fillStyle = '#666'; ctx.textAlign = 'center'; ctx.font = 'bold 20px "Titillium Web"'; ctx.fillText("RECENT RACE POSITIONS", rightX + graphW/2, graphY + graphH + 30); } else { ctx.fillStyle = '#444'; ctx.textAlign = 'center'; ctx.font = 'italic 30px "Titillium Web"'; ctx.fillText("Not enough race data for graph", rightX + graphW/2, graphY + graphH/2); }
    };

    const drawLeagueStats = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "LEAGUE RANKINGS", "Season Statistics Overview", w);
        if (!leagueStatsData) return;
        const categories = [
            { title: "FASTEST LAPS", data: leagueStatsData.fastest_lap, unit: '' },
            { title: "CLEAN RACES", data: leagueStatsData.clean_races, unit: '' },
            { title: "PENALTY KINGS", data: leagueStatsData.penalties, unit: 's' },
            { title: "PODIUMS", data: leagueStatsData.podiums, unit: '' },
            { title: "HARD CHARGERS", data: leagueStatsData.hard_charger, unit: ' pos' },
            { title: "CONSISTENCY", data: leagueStatsData.consistency, unit: ' avg' }
        ];
        const marginX = 80; const startY = 220; const cardGap = 30; const cardW = (w - (marginX * 2) - (cardGap * 2)) / 3; const cardH = 400;
        categories.forEach((cat, idx) => {
            const col = idx % 3; const row = Math.floor(idx / 3); const x = marginX + col * (cardW + cardGap); const y = startY + row * (cardH + cardGap);
            ctx.fillStyle = '#1e1e24'; roundRect(ctx, x, y, cardW, cardH, 10); ctx.fill();
            ctx.fillStyle = accentColor; ctx.fillRect(x, y, cardW, 40); ctx.fillStyle = textColor; ctx.font = '900 20px "Titillium Web"'; ctx.textAlign = 'center'; ctx.fillText(cat.title, x + cardW / 2, y + 28);
            const listData = cat.data || [];
            listData.slice(0, 5).forEach((item: any, i: number) => {
                const rowY = y + 60 + (i * 65);
                ctx.save(); ctx.globalAlpha = boxOpacity; ctx.fillStyle = '#fff'; roundRect(ctx, x + 10, rowY, cardW - 20, 55, 4); ctx.fill(); ctx.restore();
                ctx.fillStyle = item.team_color || '#666'; ctx.fillRect(x + 10, rowY + 5, 4, 45);
                ctx.fillStyle = '#000'; ctx.font = 'bold 18px "Titillium Web"'; ctx.textAlign = 'left'; ctx.fillText(String(item.username).toUpperCase(), x + 25, rowY + 35);
                ctx.fillStyle = accentColor; ctx.font = '900 22px "Titillium Web"'; ctx.textAlign = 'right'; ctx.fillText(item.value + cat.unit, x + cardW - 25, rowY + 35);
            });
        });
    };

    const drawCanvas = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const { w, h } = RATIOS[ratio];
        canvas.width = w; canvas.height = h;

        // Background
        if (bgType === 'TRANSPARENT') ctx.clearRect(0, 0, w, h);
        else if (bgType === 'SOLID') { ctx.fillStyle = bgColor; ctx.fillRect(0, 0, w, h); }
        else if (bgType === 'F1_CLEAN') { drawCleanF1Background(ctx, w, h); }
        else if (bgType === 'IMAGE' && customBgImage) {
            try {
                const bgImg = await loadImage(customBgImage);
                const scale = Math.max(w / bgImg.width, h / bgImg.height);
                const x = (w / 2) - (bgImg.width / 2) * scale;
                const y = (h / 2) - (bgImg.height / 2) * scale;
                ctx.drawImage(bgImg, x, y, bgImg.width * scale, bgImg.height * scale);
                ctx.fillStyle = 'rgba(0,0,0,0.6)'; 
                ctx.fillRect(0, 0, w, h);
            } catch { ctx.fillStyle = '#0f0f13'; ctx.fillRect(0, 0, w, h); }
        } else {
            const grad = ctx.createLinearGradient(0, 0, 0, h);
            grad.addColorStop(0, '#0f0f13'); grad.addColorStop(1, '#1a1a20');
            ctx.fillStyle = grad; ctx.fillRect(0, 0, w, h);
            
            ctx.strokeStyle = '#222'; ctx.lineWidth = 1;
            ctx.beginPath();
            for(let i=0; i<w; i+=40) { ctx.moveTo(i,0); ctx.lineTo(i-h, h); }
            ctx.stroke();
        }

        switch (activeTab) {
            case 'SEASON_LINEUP': await drawSeasonLineup(ctx, w, h); break;
            case 'RACE_GRID': await drawRaceGrid(ctx, w, h); break;
            case 'CALENDAR': 
                if (calendarView === 'GRID') await drawCalendarGrid(ctx, w, h);
                else await drawCalendarList(ctx, w, h);
                break;
            case 'STANDINGS': await drawStandings(ctx, w, h); break;
            case 'DRIVER_STANDINGS': await drawDriverStandings(ctx, w, h); break;
            case 'TEAM_STANDINGS': await drawTeamStandings(ctx, w, h); break;
            case 'RACE_RESULTS': await drawRaceResults(ctx, w, h); break;
            case 'RACE_RESULTS_DETAILED': await drawDetailedRaceResults(ctx, w, h); break;
            case 'QUALI_RESULTS': await drawQualiResults(ctx, w, h); break;
            case 'SEASON_STATS': await drawSeasonStats(ctx, w, h); break;
            case 'HEAD_TO_HEAD': await drawHeadToHead(ctx, w, h); break;
            case 'TEAMMATE_BATTLE': await drawTeammateBattle(ctx, w, h); break;
            case 'DRIVER_STATS': await drawDriverStats(ctx, w, h); break;
            case 'LEAGUE_STATS': await drawLeagueStats(ctx, w, h); break;
        }

        // Logo Overlay
        if (settings.league_logo_url) {
            try {
                const logo = await loadImage(settings.league_logo_url);
                const targetH = 100;
                const scale = targetH / logo.height;
                ctx.save(); 
                ctx.shadowColor = "rgba(0,0,0,0.8)"; 
                ctx.shadowBlur = 20;
                ctx.drawImage(logo, 50, 40, logo.width * scale, targetH);
                ctx.restore();
            } catch {}
        }
        
        ctx.font = 'bold 24px "Titillium Web", sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.4)'; 
        ctx.textAlign = 'right';
        ctx.fillText(settings.league_name.toUpperCase(), w - 50, h - 30);
    };

    const drawHeader = (ctx: CanvasRenderingContext2D, title: string, subtitle: string, w: number) => {
        ctx.font = 'italic 900 60px "Titillium Web"';
        const titleW = ctx.measureText(title.toUpperCase()).width;
        const blockW = Math.max(700, titleW + 200);
        const blockX = (w - blockW) / 2;

        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 20;
        
        ctx.fillStyle = '#000000'; 
        ctx.beginPath();
        ctx.moveTo(blockX, 30);
        ctx.lineTo(blockX + blockW, 30);
        ctx.lineTo(blockX + blockW - 40, 100);
        ctx.lineTo(blockX + 40, 100);
        ctx.fill();
        
        // --- ACCENT BAR USING SELECTED COLOR ---
        ctx.fillStyle = accentColor;
        ctx.fillRect(blockX + 40, 100, blockW - 80, 8);
        
        ctx.textAlign = 'center'; 
        ctx.fillStyle = textColor; 
        ctx.fillText(title.toUpperCase(), w/2, 85);
        
        ctx.font = 'bold 30px "Titillium Web"'; 
        ctx.fillStyle = textColor; 
        ctx.shadowColor = 'rgba(0,0,0,1)'; ctx.shadowBlur = 4;
        ctx.fillText(subtitle.toUpperCase(), w/2, 145);
        
        ctx.restore();
    };

    const drawNoData = (ctx: CanvasRenderingContext2D, w: number, h: number, msg: string) => {
        ctx.save();
        ctx.textAlign = 'center';
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.font = 'bold 40px "Titillium Web"';
        ctx.fillText(msg, w/2, h/2);
        
        ctx.font = '20px "Titillium Web"';
        ctx.fillStyle = 'rgba(255,100,100,0.8)';
        // Show actual error if present, otherwise generic message
        const subMsg = errorMsg ? errorMsg.substring(0, 50) + "..." : "Check Admin Panel data.";
        ctx.fillText(subMsg, w/2, h/2 + 40);
        ctx.restore();
    };

    // --- SEASON LINEUP ---
    const drawSeasonLineup = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "Driver Line-Up", seasonName.toUpperCase(), w);
        
        if (teams.length === 0) {
            drawNoData(ctx, w, h, "NO TEAMS FOUND");
            return;
        }

        const startY = 180;
        const colCount = 2;
        const marginX = 100;
        const gapX = 60;
        const rowGap = 30;
        const colW = (w - (marginX * 2) - ((colCount - 1) * gapX)) / colCount;
        const rowH = 140; 
        
        for (let i = 0; i < teams.length; i++) {
            const team = teams[i];
            const r = Math.floor(i / colCount);
            const c = i % colCount;
            const x = marginX + c * (colW + gapX);
            const y = startY + r * (rowH + rowGap);

            // Box
            ctx.save(); 
            ctx.globalAlpha = boxOpacity; 
            ctx.fillStyle = '#ffffff'; 
            ctx.beginPath();
            ctx.moveTo(x + 20, y);
            ctx.lineTo(x + colW, y);
            ctx.lineTo(x + colW - 20, y + rowH);
            ctx.lineTo(x, y + rowH);
            ctx.fill();
            ctx.restore();

            // Color Strip
            ctx.fillStyle = team.color || '#333'; 
            ctx.beginPath();
            ctx.moveTo(x + 20, y);
            ctx.lineTo(x + 35, y);
            ctx.lineTo(x + 15, y + rowH);
            ctx.lineTo(x, y + rowH);
            ctx.fill();

            // Drivers Logic: MAIN DRIVERS (Permanent)
            const teamDrivers = drivers.filter(d => d.teamId === team.id && d.is_reserve != 1).slice(0, 2);

            // Car
            const carUrl = team.carImageUrl || team.car_image_url;
            if (carUrl) {
                try {
                    const carImg = await loadImage(carUrl);
                    const maxCarH = 100;
                    const maxCarW = colW * 0.6;
                    const ratio = Math.min(maxCarW / carImg.width, maxCarH / carImg.height);
                    const drawCarW = carImg.width * ratio;
                    const drawCarH = carImg.height * ratio;
                    
                    ctx.save();
                    ctx.shadowColor = 'rgba(0,0,0,0.5)';
                    ctx.shadowBlur = 10;
                    ctx.shadowOffsetY = 5;
                    ctx.drawImage(carImg, x + colW - drawCarW - 20, y + rowH - drawCarH - 10, drawCarW, drawCarH);
                    ctx.restore();
                } catch {}
            }

            // Logo
            if(team.logoUrl) {
                try {
                    const l = await loadImage(team.logoUrl);
                    const s = Math.min(60/l.width, 60/l.height); 
                    ctx.drawImage(l, x+50, y+15, l.width*s, l.height*s);
                } catch{}
            }

            // Team Name
            ctx.fillStyle = '#1f1f27'; 
            ctx.font = '900 32px "Titillium Web"'; 
            ctx.textAlign = 'left';
            ctx.fillText(team.name.toUpperCase(), x + 130, y + 45);
            
            // Driver Names
            ctx.font = 'bold 24px "Titillium Web"';
            
            const d1 = teamDrivers[0];
            const d2 = teamDrivers[1];
            
            const getName = (d: any) => d ? d.username : "TBA";
            const getNum = (d: any) => d && d.raceNumber ? `#${d.raceNumber} ` : ""; 
            
            ctx.fillStyle = d1 ? '#333' : '#aaa';
            ctx.fillText(getNum(d1) + getName(d1).toUpperCase(), x + 130, y + 85);
            
            ctx.fillStyle = d2 ? '#333' : '#aaa';
            ctx.fillText(getNum(d2) + getName(d2).toUpperCase(), x + 130, y + 115);
        }
    };

    // --- RACE GRID (ENTRY LIST) ---
    const drawRaceGrid = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        const ev = events.find(e => e.id === selectedEventId);
        const sub = ev ? `ROUND ${ev.roundNumber} | ${ev.trackLocation.toUpperCase()}` : "RACE GRID";
        drawHeader(ctx, "Entry List", sub, w);
        
        if (teams.length === 0) {
            drawNoData(ctx, w, h, "NO TEAMS FOUND");
            return;
        }

        const startY = 180;
        const colCount = 2;
        const marginX = 100;
        const gapX = 60;
        const rowGap = 30;
        const colW = (w - (marginX * 2) - ((colCount - 1) * gapX)) / colCount;
        const rowH = 140; 
        
        for (let i = 0; i < teams.length; i++) {
            const team = teams[i];
            const r = Math.floor(i / colCount);
            const c = i % colCount;
            const x = marginX + c * (colW + gapX);
            const y = startY + r * (rowH + rowGap);

            // Box
            ctx.save(); 
            ctx.globalAlpha = boxOpacity; 
            ctx.fillStyle = '#ffffff'; 
            ctx.beginPath();
            ctx.moveTo(x + 20, y);
            ctx.lineTo(x + colW, y);
            ctx.lineTo(x + colW - 20, y + rowH);
            ctx.lineTo(x, y + rowH);
            ctx.fill();
            ctx.restore();

            // Color Strip
            ctx.fillStyle = team.color || '#333'; 
            ctx.beginPath();
            ctx.moveTo(x + 20, y);
            ctx.lineTo(x + 35, y);
            ctx.lineTo(x + 15, y + rowH);
            ctx.lineTo(x, y + rowH);
            ctx.fill();

            // Drivers Logic: STRICT GRID FIX
            let teamDrivers: any[] = [];
            if (gridData.length > 0) {
                 teamDrivers = gridData.filter(d => d.team_id === team.id);
            } 
            
            // Car
            const carUrl = team.carImageUrl || team.car_image_url;
            if (carUrl) {
                try {
                    const carImg = await loadImage(carUrl);
                    const maxCarH = 100;
                    const maxCarW = colW * 0.6;
                    const ratio = Math.min(maxCarW / carImg.width, maxCarH / carImg.height);
                    const drawCarW = carImg.width * ratio;
                    const drawCarH = carImg.height * ratio;
                    
                    ctx.save();
                    ctx.shadowColor = 'rgba(0,0,0,0.5)';
                    ctx.shadowBlur = 10;
                    ctx.shadowOffsetY = 5;
                    ctx.drawImage(carImg, x + colW - drawCarW - 20, y + rowH - drawCarH - 10, drawCarW, drawCarH);
                    ctx.restore();
                } catch {}
            }

            // Logo
            if(team.logoUrl) {
                try {
                    const l = await loadImage(team.logoUrl);
                    const s = Math.min(60/l.width, 60/l.height); 
                    ctx.drawImage(l, x+50, y+15, l.width*s, l.height*s);
                } catch{}
            }

            // Team Name
            ctx.fillStyle = '#1f1f27'; 
            ctx.font = '900 32px "Titillium Web"'; 
            ctx.textAlign = 'left';
            ctx.fillText(team.name.toUpperCase(), x + 130, y + 45);
            
            // Driver Names
            ctx.font = 'bold 24px "Titillium Web"';
            
            const d1 = teamDrivers[0];
            const d2 = teamDrivers[1];
            
            const getDriverLabel = (d: any) => {
                if (!d) return "TBA";
                let n = d.username.toUpperCase();
                if (d.is_reserve == 1) n += "*";
                return n;
            };
            
            ctx.fillStyle = d1 ? '#333' : '#aaa';
            ctx.fillText(getDriverLabel(d1), x + 130, y + 85);
            
            ctx.fillStyle = d2 ? '#333' : '#aaa';
            ctx.fillText(getDriverLabel(d2), x + 130, y + 115);
        }
    };

    // --- CALENDAR GRID VIEW ---
    const drawCalendarGrid = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "Season Calendar", seasonName, w);
        
        if (events.length === 0) {
            drawNoData(ctx, w, h, "NO EVENTS FOUND");
            return;
        }

        const startY = 200;
        const margin = 80;
        const gap = 30;
        
        // Auto-Calc Columns based on event count to fit width
        let cols = 4;
        if (events.length > 16) cols = 5;
        if (events.length > 20) cols = 6;
        
        // Calculate dimensions
        const rows = Math.ceil(events.length / cols);
        const boxW = (w - (margin * 2) - ((cols - 1) * gap)) / cols;
        
        // Available height
        const availableH = h - startY - 50;
        // Calculate optimal box height
        const optimalH = (availableH - ((rows - 1) * gap)) / rows;
        // Clamp height but allow it to shrink to fit
        const boxH = Math.min(220, Math.max(120, optimalH)); 
        
        // Scale content if box gets too small
        const contentScale = Math.min(1, boxH / 220);

        for (let i = 0; i < events.length; i++) {
            const evt = events[i];
            const r = Math.floor(i/cols);
            const c = i%cols;
            const x = margin + c * (boxW + gap);
            const y = startY + r * (boxH + gap);

            // Box
            ctx.save(); 
            ctx.globalAlpha = boxOpacity; 
            ctx.fillStyle = '#1a1a20'; 
            ctx.beginPath();
            ctx.roundRect(x, y, boxW, boxH, 10);
            ctx.fill();
            
            // 1. Flag Background (Scenic)
            if (evt.trackFlag) {
                try { 
                    const img = await loadImage(evt.trackFlag); 
                    ctx.save();
                    ctx.clip(); 
                    ctx.globalAlpha = 1.0;
                    
                    const sc = Math.max(boxW/img.width, boxH/img.height);
                    const offX = (boxW - (img.width * sc)) / 2;
                    const offY = (boxH - (img.height * sc)) / 2;
                    
                    ctx.drawImage(img, x + offX, y + offY, img.width*sc, img.height*sc);
                    
                    // Dark Overlay
                    ctx.fillStyle = 'rgba(0,0,0,0.40)'; 
                    ctx.fillRect(x, y, boxW, boxH);
                    
                    ctx.restore();
                } catch {}
            }
            ctx.restore(); // Restore box context

            // 2. Track Layout (Foreground)
            if (evt.trackLayout || evt.layoutUrl) { 
                 const lUrl = evt.trackLayout || evt.layoutUrl;
                 try {
                     const lImg = await loadImage(lUrl);
                     const maxH = boxH * 0.7;
                     const maxW = boxW * 0.8;
                     const sc = Math.min(maxW/lImg.width, maxH/lImg.height);
                     
                     ctx.save();
                     ctx.globalAlpha = 0.4;
                     const lx = x + (boxW - lImg.width * sc) / 2;
                     const ly = y + (boxH - lImg.height * sc) / 2;
                     ctx.drawImage(lImg, lx, ly, lImg.width*sc, lImg.height*sc);
                     ctx.restore();
                 } catch {}
            }

            // Badge Setup
            const isTest = evt.isTestRace;
            const badgeSize = 60 * contentScale;
            const badgeX = x + 20 * contentScale;
            const badgeY = y + 20 * contentScale;

            ctx.fillStyle = isTest ? '#3b82f6' : accentColor; 
            ctx.beginPath();
            ctx.roundRect(badgeX, badgeY, badgeSize, badgeSize, 8 * contentScale);
            ctx.fill();
            
            ctx.fillStyle='#fff'; 
            ctx.textAlign='center'; 
            
            if (isTest) {
                ctx.font=`bold ${12 * contentScale}px "Titillium Web"`;
                ctx.fillText("TEST", badgeX + badgeSize/2, badgeY + badgeSize * 0.35);
                ctx.font=`900 ${20 * contentScale}px "Titillium Web"`; 
                ctx.fillText("RACE", badgeX + badgeSize/2, badgeY + badgeSize * 0.75);
            } else {
                ctx.font=`bold ${14 * contentScale}px "Titillium Web"`; 
                ctx.fillText("ROUND", badgeX + badgeSize/2, badgeY + badgeSize * 0.35);
                ctx.font=`900 ${32 * contentScale}px "Titillium Web"`; 
                ctx.fillText(evt.roundNumber.toString(), badgeX + badgeSize/2, badgeY + badgeSize * 0.85);
            }
            
            // Location Info
            const textY = y + (140 * contentScale);
            ctx.textAlign='left'; 
            ctx.font=`900 ${28 * contentScale}px "Titillium Web"`; 
            ctx.fillStyle = '#fff';
            ctx.shadowColor="black"; ctx.shadowBlur=4;
            ctx.fillText(evt.trackLocation?.toUpperCase(), x + 20, textY);
            
            const dateY = y + (170 * contentScale);
            ctx.font=`bold ${20 * contentScale}px "Titillium Web"`; 
            ctx.fillStyle = accentColor;
            const dateStr = new Date(evt.date).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});
            ctx.fillText(dateStr, x + 20, dateY);
        }
    };

    // --- CALENDAR LIST VIEW ---
    const drawCalendarList = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "Season Calendar", seasonName, w);
        
        if (events.length === 0) {
            drawNoData(ctx, w, h, "NO EVENTS FOUND");
            return;
        }

        const startY = 180;
        const marginX = 100;
        const contentW = w - (marginX * 2);
        
        const useDoubleCol = events.length > 10;
        
        // Calculate Row Height to fit vertical space
        const rowsPerCol = useDoubleCol ? Math.ceil(events.length / 2) : events.length;
        const availableH = h - startY - 50;
        const gapY = 15;
        
        let rowH = (availableH - ((rowsPerCol - 1) * gapY)) / rowsPerCol;
        rowH = Math.min(80, Math.max(50, rowH)); // Clamp
        
        const contentScale = Math.min(1, rowH / 80);
        const colW = useDoubleCol ? (contentW - 40) / 2 : contentW;
        
        for (let i = 0; i < events.length; i++) {
            const evt = events[i];
            
            let x = marginX;
            let y = startY + i * (rowH + gapY);

            if (useDoubleCol) {
                const mid = Math.ceil(events.length / 2);
                if (i >= mid) {
                    x = marginX + colW + 40;
                    y = startY + (i - mid) * (rowH + gapY);
                } else {
                    y = startY + i * (rowH + gapY);
                }
            }

            // Row Background
            ctx.save();
            ctx.globalAlpha = boxOpacity;
            ctx.fillStyle = '#1a1a20'; // Base dark
            ctx.beginPath();
            ctx.roundRect(x, y, colW, rowH, 8);
            ctx.fill();
            
            // 1. Flag Background
            if (evt.trackFlag) {
                try {
                    const img = await loadImage(evt.trackFlag);
                    ctx.save();
                    ctx.clip(); // Clip to row
                    
                    const scale = Math.max(colW/img.width, rowH/img.height);
                    ctx.drawImage(img, x, y, img.width*scale, img.height*scale);
                    
                    ctx.fillStyle = 'rgba(0,0,0,0.40)'; 
                    ctx.fillRect(x, y, colW, rowH);
                    
                    ctx.restore();
                } catch {}
            }
            
            ctx.restore(); 

            // Accent Bar
            ctx.fillStyle = evt.isTestRace ? '#3b82f6' : accentColor;
            ctx.beginPath();
            ctx.roundRect(x, y, 10, rowH, [8,0,0,8]);
            ctx.fill();

            // Round Number Circle
            const circleSize = 25 * contentScale;
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(x + 50, y + rowH/2, circleSize, 0, 2 * Math.PI);
            ctx.fill();
            
            ctx.fillStyle = '#1f1f27';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = `bold ${24 * contentScale}px "Titillium Web"`;
            
            if (evt.isTestRace) {
                ctx.font = `bold ${14 * contentScale}px "Titillium Web"`;
                ctx.fillText("T", x + 50, y + rowH/2 + 1);
            } else {
                ctx.fillText(evt.roundNumber.toString(), x + 50, y + rowH/2 + 2);
            }
            ctx.textBaseline = 'alphabetic'; 

            // Text Info
            ctx.textAlign = 'left';
            ctx.fillStyle = '#fff';
            ctx.font = `900 ${32 * contentScale}px "Titillium Web"`;
            ctx.fillText(evt.trackLocation?.toUpperCase(), x + 90, y + rowH * 0.5);

            ctx.fillStyle = '#aaa';
            ctx.font = `bold ${20 * contentScale}px "Titillium Web"`;
            const dateStr = new Date(evt.date).toLocaleDateString('de-DE', {weekday: 'short', day: '2-digit', month: 'long'});
            
            let extraInfo = "";
            if (evt.isSprint) extraInfo += " • SPRINT";
            if (evt.isTestRace) extraInfo += " • TEST RACE";
            
            ctx.fillText(dateStr + extraInfo, x + 90, y + rowH * 0.85);

            // 2. Track Layout (Right Side)
            if (evt.trackLayout || evt.layoutUrl) { 
                 const lUrl = evt.trackLayout || evt.layoutUrl;
                 try {
                     const lImg = await loadImage(lUrl);
                     const maxH = rowH * 0.8;
                     const scale = maxH / lImg.height;
                     const drawW = lImg.width * scale;
                     
                     ctx.save();
                     ctx.drawImage(lImg, x + colW - drawW - 20, y + (rowH - maxH) / 2, drawW, maxH);
                     ctx.restore();
                 } catch {}
            }
        }
    };

    // --- NEW: COMBINED STANDINGS (DRIVERS & TEAMS) ---
    const drawStandings = async (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        drawHeader(ctx, "Championship Overview", seasonName, w);
        
        if (standings.length === 0) {
            drawNoData(ctx, w, h, "NO STANDINGS FOUND");
            return;
        }

        const startY = 180;
        const padding = 60;
        const gapBetweenTables = 60;
        
        // 60% Width for Drivers, ~40% for Teams
        const availableW = w - (padding * 2) - gapBetweenTables;
        const driverW = availableW * 0.60;
        const teamW = availableW * 0.40;
        
        const rowH = 45;
        const driverX = padding;
        const teamX = padding + driverW + gapBetweenTables;

        // --- DRIVERS COLUMN ---
        ctx.fillStyle = '#111'; 
        ctx.fillRect(driverX, startY, driverW, 40);
        ctx.fillStyle = '#aaa';
        ctx.font = 'bold 18px "Titillium Web"';
        ctx.textAlign = 'left';
        ctx.fillText("POS", driverX + 20, startY + 27);
        ctx.fillText("DRIVER", driverX + 80, startY + 27);
        ctx.textAlign = 'right'; 
        ctx.fillText("POINTS", driverX + driverW - 20, startY + 27);

        // Limit to 20 drivers to fit screen
        const maxDrivers = Math.min(22, standings.length);

        for(let i=0; i<maxDrivers; i++) {
            const d = standings[i];
            const y = startY + 45 + (i * (rowH + 4));
            
            ctx.save(); 
            ctx.globalAlpha = boxOpacity * (i % 2 === 0 ? 0.9 : 0.7); 
            ctx.fillStyle = '#ffffff'; 
            ctx.beginPath(); ctx.roundRect(driverX, y, driverW, rowH, 4); ctx.fill();
            ctx.restore();

            // Pos
            ctx.fillStyle = accentColor;
            ctx.font = '900 24px "Titillium Web"';
            ctx.textAlign = 'center'; 
            ctx.fillText((i+1).toString(), driverX + 35, y + 32);
            
            // Color Bar
            ctx.fillStyle = d.teamColor || '#666'; 
            ctx.fillRect(driverX + 60, y + 8, 6, rowH - 16);

            // Name
            ctx.fillStyle = '#111'; 
            ctx.font = 'bold 22px "Titillium Web"';
            ctx.textAlign = 'left'; 
            ctx.fillText(d.username.toUpperCase(), driverX + 80, y + 32);
            
            // Team Name (Small)
            ctx.fillStyle = '#666';
            ctx.font = 'bold 14px "Titillium Web"';
            ctx.fillText(d.teamName?.toUpperCase() || "", driverX + 350, y + 29);

            // Points
            ctx.textAlign = 'right'; 
            ctx.fillStyle = '#000';
            ctx.font = '900 24px "Titillium Web"';
            ctx.fillText(parseFloat(d.totalPoints).toFixed(0), driverX + driverW - 20, y + 32);
        }

        // --- CONSTRUCTORS COLUMN ---
        ctx.fillStyle = '#111'; 
        ctx.fillRect(teamX, startY, teamW, 40);
        ctx.fillStyle = '#aaa';
        ctx.font = 'bold 18px "Titillium Web"';
        ctx.textAlign = 'left';
        ctx.fillText("POS", teamX + 20, startY + 27);
        ctx.fillText("TEAM", teamX + 80, startY + 27);
        ctx.textAlign = 'right'; 
        ctx.fillText("POINTS", teamX + teamW - 20, startY + 27);

        const maxTeams = Math.min(12, constructorStandings.length);

        for(let i=0; i<maxTeams; i++) {
            const t = constructorStandings[i];
            const y = startY + 45 + (i * (rowH + 4));
            
            ctx.save(); 
            ctx.globalAlpha = boxOpacity * (i % 2 === 0 ? 0.9 : 0.7); 
            ctx.fillStyle = '#ffffff'; 
            ctx.beginPath(); ctx.roundRect(teamX, y, teamW, rowH, 4); ctx.fill();
            ctx.restore();

            // Pos
            ctx.fillStyle = accentColor;
            ctx.font = '900 24px "Titillium Web"';
            ctx.textAlign = 'center'; 
            ctx.fillText((i+1).toString(), teamX + 35, y + 32);
            
            // Color Bar
            ctx.fillStyle = t.teamColor || '#666'; 
            ctx.fillRect(teamX + 60, y + 8, 6, rowH - 16);

            // Team Name
            ctx.fillStyle = '#111'; 
            ctx.font = 'bold 22px "Titillium Web"';
            ctx.textAlign = 'left'; 
            ctx.fillText(t.name.toUpperCase(), teamX + 80, y + 32);

            // Points
            ctx.textAlign = 'right'; 
            ctx.fillStyle = '#000';
            ctx.font = '900 24px "Titillium Web"';
            ctx.fillText(parseFloat(t.totalPoints).toFixed(0), teamX + teamW - 20, y + 32);
        }
    };

    const handleDownload = () => {
        const c = canvasRef.current; if (!c) return;
        const l = document.createElement('a'); l.download = `SDC_${activeTab}.png`; l.href = c.toDataURL('image/png'); l.click();
    };

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic">Grafik-Center</h1>
                    <p className="text-gray-400 text-sm">Create Social Media Graphics</p>
                </div>
                    <div className="flex bg-gray-800 rounded p-1 gap-1 flex-wrap max-w-full overflow-x-auto">
                        <button onClick={() => setActiveTab('SEASON_LINEUP')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='SEASON_LINEUP'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><LayoutGrid size={12}/> Line-Up</button>
                        <button onClick={() => setActiveTab('RACE_GRID')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='RACE_GRID'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Grid size={12}/> Entry List</button>
                        <button onClick={() => setActiveTab('CALENDAR')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='CALENDAR'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Calendar size={12}/> Calendar</button>
                        <button onClick={() => setActiveTab('STANDINGS')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='STANDINGS'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Trophy size={12}/> Standings</button>
                        
                        <div className="w-px h-6 bg-gray-700 mx-1 self-center" />
                        
                        <button onClick={() => setActiveTab('DRIVER_STANDINGS')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='DRIVER_STANDINGS'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><User size={12}/> Drivers</button>
                        <button onClick={() => setActiveTab('TEAM_STANDINGS')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='TEAM_STANDINGS'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Monitor size={12}/> Teams</button>
                        <button onClick={() => setActiveTab('RACE_RESULTS')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='RACE_RESULTS'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Flag size={12}/> Results</button>
                        <button onClick={() => setActiveTab('RACE_RESULTS_DETAILED')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='RACE_RESULTS_DETAILED'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><List size={12}/> Detailed</button>
                        <button onClick={() => setActiveTab('QUALI_RESULTS')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='QUALI_RESULTS'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Timer size={12}/> Quali</button>
                        <button onClick={() => setActiveTab('SEASON_STATS')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='SEASON_STATS'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Activity size={12}/> Insights</button>
                        <button onClick={() => setActiveTab('HEAD_TO_HEAD')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='HEAD_TO_HEAD'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Swords size={12}/> H2H</button>
                        <button onClick={() => setActiveTab('TEAMMATE_BATTLE')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='TEAMMATE_BATTLE'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><BarChart2 size={12}/> Teammate</button>
                        <button onClick={() => setActiveTab('DRIVER_STATS')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='DRIVER_STATS'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><FileText size={12}/> Driver Stats</button>
                        <button onClick={() => setActiveTab('LEAGUE_STATS')} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase flex items-center gap-2 whitespace-nowrap ${activeTab==='LEAGUE_STATS'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Trophy size={12}/> League Stats</button>
                    </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <Card title="Settings" className="space-y-6">
                    <div className="flex justify-between items-center bg-gray-900/50 p-2 rounded mb-2 border border-gray-800">
                        <span className="text-[10px] text-gray-500 font-bold uppercase flex items-center gap-1">
                            {errorMsg ? <AlertTriangle size={12} className="text-red-500"/> : <Monitor size={12}/>}
                            Status
                        </span>
                        <div className="flex gap-2">
                             <Button size="sm" variant="secondary" className="text-[10px] px-2 h-6" onClick={loadData} icon={RefreshCw}>Refresh</Button>
                        </div>
                    </div>
                    {errorMsg && (
                        <div className="text-[10px] text-red-400 bg-red-900/20 p-2 rounded border border-red-900/50 mb-2 font-mono">
                            {errorMsg.substring(0, 100)}...
                        </div>
                    )}

                    {['RACE_GRID', 'RACE_RESULTS', 'RACE_RESULTS_DETAILED', 'QUALI_RESULTS'].includes(activeTab) && (
                        <div className="bg-f1-red/10 border border-f1-red/30 p-3 rounded mb-2">
                            <label className="text-[10px] font-bold text-f1-red uppercase mb-1 block">Rennen auswählen</label>
                            <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-xs" value={selectedEventId} onChange={e => setSelectedEventId(e.target.value)}>
                                {events.map(e => <option key={e.id} value={e.id}>{e.trackLocation} (R{e.roundNumber})</option>)}
                            </select>
                            
                            {['RACE_RESULTS', 'RACE_RESULTS_DETAILED'].includes(activeTab) && events.find(e => e.id === selectedEventId)?.isSprint && (
                                <div className="mt-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Session</label>
                                    <div className="flex gap-1">
                                        <button onClick={() => setSelectedSession('RACE')} className={`flex-1 py-1 rounded text-[10px] font-bold ${selectedSession==='RACE'?'bg-f1-red text-white':'bg-gray-800 text-gray-400'}`}>RACE</button>
                                        <button onClick={() => setSelectedSession('SPRINT')} className={`flex-1 py-1 rounded text-[10px] font-bold ${selectedSession==='SPRINT'?'bg-f1-red text-white':'bg-gray-800 text-gray-400'}`}>SPRINT</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'DRIVER_STATS' && (
                        <div className="bg-gray-800 p-3 rounded border border-gray-700 mb-2">
                            <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Fahrer auswählen</label>
                            <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-xs" value={selectedDriverId} onChange={e => setSelectedDriverId(e.target.value)}>
                                <option value="">-- Select Driver --</option>
                                {drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>)}
                            </select>
                        </div>
                    )}

                    {activeTab === 'HEAD_TO_HEAD' && (
                        <div className="bg-gray-800 p-3 rounded border border-gray-700 mb-2 space-y-3">
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Modus</label>
                                <div className="flex gap-1">
                                    <button onClick={() => setH2HMode('DRIVER')} className={`flex-1 py-1 rounded text-[10px] font-bold ${h2hMode==='DRIVER'?'bg-f1-red text-white':'bg-gray-900 text-gray-400'}`}>DRIVER</button>
                                    <button onClick={() => setH2HMode('TEAM')} className={`flex-1 py-1 rounded text-[10px] font-bold ${h2hMode==='TEAM'?'bg-f1-red text-white':'bg-gray-900 text-gray-400'}`}>TEAM</button>
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">{h2hMode === 'DRIVER' ? 'Fahrer 1' : 'Team 1'}</label>
                                <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-xs" value={h2hId1} onChange={e => setH2HId1(e.target.value)}>
                                    <option value="">-- Select --</option>
                                    {h2hMode === 'DRIVER' ? drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>) : teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">{h2hMode === 'DRIVER' ? 'Fahrer 2' : 'Team 2'}</label>
                                <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-xs" value={h2hId2} onChange={e => setH2HId2(e.target.value)}>
                                    <option value="">-- Select --</option>
                                    {h2hMode === 'DRIVER' ? drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>) : teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'CALENDAR' && (
                        <div className="bg-gray-800 p-3 rounded border border-gray-700">
                             <label className="text-[10px] font-bold text-gray-400 uppercase mb-2 block">Layout Style</label>
                             <div className="flex gap-2">
                                <button onClick={() => setCalendarView('GRID')} className={`flex-1 py-2 rounded text-xs font-bold flex items-center justify-center gap-2 ${calendarView === 'GRID' ? 'bg-f1-red text-white' : 'bg-gray-900 text-gray-400'}`}>
                                    <LayoutGrid size={14}/> Grid
                                </button>
                                <button onClick={() => setCalendarView('LIST')} className={`flex-1 py-2 rounded text-xs font-bold flex items-center justify-center gap-2 ${calendarView === 'LIST' ? 'bg-f1-red text-white' : 'bg-gray-900 text-gray-400'}`}>
                                    <AlignJustify size={14}/> List
                                </button>
                             </div>
                        </div>
                    )}

                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block flex items-center gap-2"><Palette size={14}/> Background</label>
                        <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white" value={bgType} onChange={e => setBgType(e.target.value as any)}>
                            <option value="DARK">SDC Dark Theme</option>
                            <option value="F1_CLEAN">F1 Clean White</option>
                            <option value="SOLID">Solid Color</option>
                            <option value="IMAGE">Custom Image</option>
                            <option value="TRANSPARENT">Transparent</option>
                        </select>
                    </div>
                    {bgType === 'SOLID' && <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} className="w-full h-10 cursor-pointer"/>}
                    
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block flex items-center gap-2"><Type size={14}/> Text Color</label>
                        <div className="flex gap-2 items-center">
                            <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} className="h-8 w-12 cursor-pointer bg-transparent border-0"/>
                            <span className="text-xs font-mono text-gray-400">{textColor}</span>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block flex items-center gap-2"><Highlighter size={14}/> Header Accent</label>
                        <div className="flex gap-2 items-center">
                            <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)} className="h-8 w-12 cursor-pointer bg-transparent border-0"/>
                            <span className="text-xs font-mono text-gray-400">{accentColor}</span>
                        </div>
                    </div>

                    {bgType === 'IMAGE' && (
                        <div>
                             <input type="text" className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-xs text-white mb-2" placeholder="Image URL..." value={customBgImage || ''} onChange={e => setCustomBgImage(e.target.value)}/>
                             <input type="file" className="text-xs text-gray-400" onChange={e => {
                                 if(e.target.files?.[0]) {
                                     const r = new FileReader(); r.onload=()=>setCustomBgImage(r.result as string); r.readAsDataURL(e.target.files[0]);
                                 }
                             }}/>
                        </div>
                    )}
                    
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block flex items-center gap-2"><Eye size={14}/> Box Opacity ({Math.round(boxOpacity*100)}%)</label>
                        <input type="range" min="0" max="1" step="0.05" value={boxOpacity} onChange={e=>setBoxOpacity(parseFloat(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-f1-red"/>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block flex items-center gap-2"><Ratio size={14}/> Format</label>
                        <div className="grid grid-cols-2 gap-2">
                            {(Object.keys(RATIOS) as RatioType[]).map(r => (
                                <button key={r} onClick={()=>setRatio(r)} className={`p-2 rounded border text-xs font-bold flex flex-col items-center gap-1 ${ratio===r?'bg-white text-black':'bg-gray-800 text-gray-400'}`}>{React.createElement(RATIOS[r].icon,{size:16})} {r}</button>
                            ))}
                        </div>
                    </div>
                    <Button className="w-full justify-center" onClick={handleDownload} icon={Download}>Download</Button>
                </Card>
                <div className="lg:col-span-3 bg-[#0a0a0a] rounded-xl border border-gray-800 p-4 flex items-center justify-center overflow-hidden min-h-[600px] relative" ref={containerRef}>
                    <div style={{ transform: `scale(${scaleFactor})`, transformOrigin: 'center', border: '1px solid #333', boxShadow: '0 0 50px rgba(0,0,0,0.5)' }}>
                        <canvas ref={canvasRef} />
                    </div>
                </div>
            </div>
        </div>
    );
};
